import React, { useEffect, useState } from "react";
import {
  Card,
  Descriptions,
  Spin,
  message,
  Table,
  Button,
  Modal,
  Select,
  Form,
} from "antd";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";

const { Option } = Select;

const TeamDetails = () => {
  const { id } = useParams(); // team_id from URL
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Fetch team details
  const fetchTeamDetails = async () => {
    setLoading(true);
    try {
      const teamRes = await api.get(`/teams/${id}`);
      setTeam(teamRes.data.data);

      // Fetch players of this team
      const playersRes = await api.get(`/team-players`);
      const filteredPlayers = playersRes.data.data.filter(
        (tp) => tp.team_id === parseInt(id)
      );

      // Extract player info
      const mappedPlayers = filteredPlayers.map((tp) => ({
        id: tp.id,
        player_id: tp.Player?.player_id,
        name: tp.Player?.name,
        is_playing: tp.is_playing,
        shirt_number: tp.shirt_number,
      }));

      setPlayers(mappedPlayers);
    } catch (err) {
      console.error(err);
      message.error("Failed to load team details");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all available players
  const fetchAllPlayers = async () => {
    try {
      const res = await api.get(`/players`);
      setAllPlayers(res.data.data);
    } catch (err) {
      console.error(err);
      message.error("Failed to load players list");
    }
  };

  // Add players to team
  const handleAddPlayers = async (values) => {
    try {
      const { player_ids } = values;
      const payload = player_ids.map((pid) => ({
        team_id: parseInt(id),
        player_id: pid,
      }));

      await api.post("/team-players/bulk", payload);
      message.success("Players added successfully");

      setIsModalVisible(false);
      form.resetFields();
      fetchTeamDetails();
    } catch (err) {
      console.error(err);
      message.error("Failed to add players");
    }
  };

  useEffect(() => {
    fetchTeamDetails();
    fetchAllPlayers();
  }, [id]);

  if (loading || !team) {
    return (
      <div style={{ textAlign: "center", marginTop: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  const playerColumns = [
    { title: "Player ID", dataIndex: "player_id", key: "player_id" },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Is Playing",
      dataIndex: "is_playing",
      key: "is_playing",
      render: (val) => (val ? "Yes" : "No"),
    },
    { title: "Shirt Number", dataIndex: "shirt_number", key: "shirt_number" },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button type="link" onClick={() => navigate(-1)}>
        ← Back to Teams
      </Button>

      <Card
        title={`Team Details - ${team.name}`}
        style={{ marginTop: 16 }}
        bordered
        extra={
          <Button type="primary" onClick={() => setIsModalVisible(true)}>
            Add Players
          </Button>
        }
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item label="Team ID">{team.team_id}</Descriptions.Item>
          <Descriptions.Item label="Team Name">{team.name}</Descriptions.Item>
          <Descriptions.Item label="Match">
            {team.match?.name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Captain">
            {team.captain?.name || "Not Assigned"}
          </Descriptions.Item>
        </Descriptions>

        <h3 style={{ marginTop: 24 }}>Players</h3>
        <Table
          columns={playerColumns}
          dataSource={players}
          rowKey="id"
          pagination={false}
          bordered
        />
      </Card>

      {/* Modal for Adding Players */}
      <Modal
        title="Add Players to Team"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        okText="Add Players"
      >
        <Form form={form} layout="vertical" onFinish={handleAddPlayers}>
          <Form.Item
            name="player_ids"
            label="Select Players"
            rules={[
              {
                required: true,
                message: "Please select at least one player",
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select players"
              showSearch
              optionFilterProp="children"
            >
              {allPlayers.map((p) => (
                <Option key={p.player_id} value={p.player_id}>
                  {p.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamDetails;
